/**
 * Play Station listing spec (play-station-generation-type Phase 1+).
 *
 * Canonical id: `play-station`. Legacy gaming ids are aliases (dual-read).
 * Required: `play_station_generation` ∈ ps2 | ps3 | ps4 | ps5.
 * Optional: `play_station_edition` ∈ slim | pro | fat | digital (pricing secondary).
 * Optional: `play_station_pad_count`, `play_station_games_count` (sell extras only).
 * Title: `{Brand?} PlayStation {N} {Edition?}{ with N pad(s)}{ and M game(s)}`.
 */

import {
  composeListingTitleFromSlotsWithIdentity,
  joinListingTitleParts,
  type ListingTitleIdentity,
} from "@/lib/listing/listingTitleTokens";
import { asListingSpecAttributes } from "@/lib/listing/listingAttributesJson";
import type {
  ListingSpecAttributes,
  ListingSpecValidationIssue,
} from "@/lib/listing/specRequirements";

export const PLAY_STATION_GENERATIONS = ["ps2", "ps3", "ps4", "ps5"] as const;
export type PlayStationGeneration = (typeof PLAY_STATION_GENERATIONS)[number];

export const CANONICAL_PLAY_STATION_SUBCATEGORY_ID = "play-station" as const;

export const LEGACY_PLAY_STATION_SUBCATEGORY_IDS = [
  "gaming-consoles",
  "gaming-console",
  "play-stations",
] as const;

export type LegacyPlayStationSubcategoryId =
  (typeof LEGACY_PLAY_STATION_SUBCATEGORY_IDS)[number];

export const PLAY_STATION_SUBCATEGORY_IDS = [
  CANONICAL_PLAY_STATION_SUBCATEGORY_ID,
  ...LEGACY_PLAY_STATION_SUBCATEGORY_IDS,
] as const;

export type PlayStationSubcategoryId =
  (typeof PLAY_STATION_SUBCATEGORY_IDS)[number];

export const PLAY_STATION_PAD_COUNT_MIN = 0;
export const PLAY_STATION_PAD_COUNT_MAX = 8;
export const PLAY_STATION_GAMES_COUNT_MIN = 0;
export const PLAY_STATION_GAMES_COUNT_MAX = 20;

/** Edition axis (optional). Infer precedence: digital → pro → slim → fat. */
export const PLAY_STATION_EDITIONS = ["slim", "pro", "fat", "digital"] as const;
export type PlayStationEdition = (typeof PLAY_STATION_EDITIONS)[number];

export type PlayStationSpecFormState = {
  playStationGeneration: PlayStationGeneration | "";
  playStationEdition: PlayStationEdition | "";
  padCount: number | "";
  gamesCount: number | "";
};

export function defaultPlayStationSpecFormState(): PlayStationSpecFormState {
  return {
    playStationGeneration: "",
    playStationEdition: "",
    padCount: "",
    gamesCount: "",
  };
}

function isOptionalPlayStationCount(
  value: unknown,
  min: number,
  max: number,
): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    Number.isInteger(value) &&
    value >= min &&
    value <= max
  );
}

export function isPlayStationPadCount(value: unknown): value is number {
  return isOptionalPlayStationCount(
    value,
    PLAY_STATION_PAD_COUNT_MIN,
    PLAY_STATION_PAD_COUNT_MAX,
  );
}

export function isPlayStationGamesCount(value: unknown): value is number {
  return isOptionalPlayStationCount(
    value,
    PLAY_STATION_GAMES_COUNT_MIN,
    PLAY_STATION_GAMES_COUNT_MAX,
  );
}

function formatCountNoun(
  count: number,
  singular: string,
  plural: string,
): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Optional title suffix: "with 2 pads and 8 games". */
export function formatPlayStationExtrasTitlePart(
  attrs: ListingSpecAttributes,
): string | undefined {
  const pad = attrs.play_station_pad_count;
  const games = attrs.play_station_games_count;
  const hasPad = isPlayStationPadCount(pad);
  const hasGames = isPlayStationGamesCount(games);
  if (!hasPad && !hasGames) return undefined;

  const bits: string[] = [];
  if (hasPad) bits.push(formatCountNoun(pad, "pad", "pads"));
  if (hasGames) bits.push(formatCountNoun(games, "game", "games"));
  return `with ${bits.join(" and ")}`;
}

export function isPlayStationGeneration(
  value: unknown,
): value is PlayStationGeneration {
  return (
    typeof value === "string" &&
    (PLAY_STATION_GENERATIONS as readonly string[]).includes(value)
  );
}

export function isPlayStationEdition(
  value: unknown,
): value is PlayStationEdition {
  return (
    typeof value === "string" &&
    (PLAY_STATION_EDITIONS as readonly string[]).includes(value)
  );
}

/**
 * Title edition cue. Precedence: digital → pro → slim → fat.
 * Word-boundary style (avoids matching inside other tokens).
 */
export function inferPlayStationEditionFromTitle(
  title: string | null | undefined,
): PlayStationEdition | null {
  const t = String(title ?? "").toLowerCase();
  if (!t.trim()) return null;

  const cue = (word: string) =>
    new RegExp(`(^|[^a-z0-9])${word}([^a-z0-9]|$)`, "i");

  if (cue("digital").test(t)) return "digital";
  if (cue("pro").test(t)) return "pro";
  if (cue("slim").test(t)) return "slim";
  if (cue("fat").test(t)) return "fat";
  return null;
}

export function resolvePlayStationEdition(
  attributes: unknown,
  title?: string | null,
): PlayStationEdition | null {
  const attrs = asListingSpecAttributes(attributes);
  if (isPlayStationEdition(attrs.play_station_edition)) {
    return attrs.play_station_edition;
  }
  return inferPlayStationEditionFromTitle(title);
}

export function formatPlayStationEditionTitleWord(
  edition: PlayStationEdition,
): string {
  switch (edition) {
    case "slim":
      return "Slim";
    case "pro":
      return "Pro";
    case "fat":
      return "Fat";
    case "digital":
      return "Digital";
  }
}

export function formatPlayStationEditionLabel(
  edition: PlayStationEdition,
): string {
  return formatPlayStationEditionTitleWord(edition);
}

export function isPlayStationSubcategory(
  subcategoryId: string | null | undefined,
): subcategoryId is PlayStationSubcategoryId {
  return (
    typeof subcategoryId === "string" &&
    (PLAY_STATION_SUBCATEGORY_IDS as readonly string[]).includes(subcategoryId)
  );
}

export function isCanonicalPlayStationSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return subcategoryId === CANONICAL_PLAY_STATION_SUBCATEGORY_ID;
}

export function getPlayStationSpecSectionLabel(): string {
  return "Play Station details";
}

export function resolveCanonicalPlayStationSubcategoryId(
  subcategoryId: string | null | undefined,
): string | null {
  if (!subcategoryId) return null;
  if (subcategoryId === CANONICAL_PLAY_STATION_SUBCATEGORY_ID) {
    return CANONICAL_PLAY_STATION_SUBCATEGORY_ID;
  }
  if (
    (LEGACY_PLAY_STATION_SUBCATEGORY_IDS as readonly string[]).includes(
      subcategoryId,
    )
  ) {
    return CANONICAL_PLAY_STATION_SUBCATEGORY_ID;
  }
  return null;
}

/**
 * D3 title inference. Precedence: PS5 → PS4 → PS3 → PS2.
 * Excludes Vita / PSP. Returns null when no generation cue.
 */
export function inferPlayStationGenerationFromTitle(
  title: string | null | undefined,
): PlayStationGeneration | null {
  const t = String(title ?? "").toLowerCase();
  if (!t.trim()) return null;
  if (/\bvita\b/.test(t) || /\bpsp\b/.test(t)) return null;

  const cue = (n: 2 | 3 | 4 | 5) =>
    new RegExp(
      `(^|[^a-z0-9])(ps\\s*${n}|playstation\\s*${n}|play\\s*station\\s*${n})([^a-z0-9]|$)`,
      "i",
    );

  if (cue(5).test(t)) return "ps5";
  if (cue(4).test(t)) return "ps4";
  if (cue(3).test(t)) return "ps3";
  if (cue(2).test(t)) return "ps2";
  return null;
}

/**
 * Attrs first, then title infer. Legacy ids alone do not imply a generation.
 */
export function resolvePlayStationGeneration(
  attributes: unknown,
  title?: string | null,
): PlayStationGeneration | null {
  const attrs = asListingSpecAttributes(attributes);
  if (isPlayStationGeneration(attrs.play_station_generation)) {
    return attrs.play_station_generation;
  }
  return inferPlayStationGenerationFromTitle(title);
}

export function formatPlayStationGenerationTitleWord(
  generation: PlayStationGeneration,
): string {
  switch (generation) {
    case "ps2":
      return "PlayStation 2";
    case "ps3":
      return "PlayStation 3";
    case "ps4":
      return "PlayStation 4";
    case "ps5":
      return "PlayStation 5";
  }
}

export function formatPlayStationGenerationLabel(
  generation: PlayStationGeneration,
): string {
  switch (generation) {
    case "ps2":
      return "PS2";
    case "ps3":
      return "PS3";
    case "ps4":
      return "PS4";
    case "ps5":
      return "PS5";
  }
}

/**
 * Locked: brand? → PlayStation {N} {Edition?}{ extras}
 */
export function composePlayStationTitleFromAttributes(
  attrs: ListingSpecAttributes,
  identity?: ListingTitleIdentity | null,
): string {
  const generation = attrs.play_station_generation;
  if (!isPlayStationGeneration(generation)) return "";

  const edition = isPlayStationEdition(attrs.play_station_edition)
    ? formatPlayStationEditionTitleWord(attrs.play_station_edition)
    : undefined;

  return composeListingTitleFromSlotsWithIdentity(
    {
      product: joinListingTitleParts(
        formatPlayStationGenerationTitleWord(generation),
        edition,
      ),
      extras: formatPlayStationExtrasTitlePart(attrs) || undefined,
    },
    identity,
  );
}

export function buildPlayStationAttributesFromForm(
  form: PlayStationSpecFormState,
): ListingSpecAttributes {
  const out: ListingSpecAttributes = {};
  if (isPlayStationGeneration(form.playStationGeneration)) {
    out.play_station_generation = form.playStationGeneration;
  }
  if (isPlayStationEdition(form.playStationEdition)) {
    out.play_station_edition = form.playStationEdition;
  }
  if (isPlayStationPadCount(form.padCount)) {
    out.play_station_pad_count = form.padCount;
  }
  if (isPlayStationGamesCount(form.gamesCount)) {
    out.play_station_games_count = form.gamesCount;
  }
  return out;
}

export function getPlayStationSpecFormValidationMessage(
  form: PlayStationSpecFormState,
): string | undefined {
  const issues = validateStoredPlayStationAttributes(
    buildPlayStationAttributesFromForm(form),
  );
  return issues[0]?.message;
}

export type PlayStationFormHydrateOptions = {
  subcategoryId?: string | null;
  title?: string | null;
};

/** Hydrate sell/edit form from stored attrs (+ title infer fallback). */
export function playStationFormFromAttributes(
  attributes: unknown,
  options?: PlayStationFormHydrateOptions,
): PlayStationSpecFormState {
  const base = defaultPlayStationSpecFormState();
  const attrs = asListingSpecAttributes(attributes);
  let playStationGeneration: PlayStationGeneration | "" =
    isPlayStationGeneration(attrs.play_station_generation)
      ? attrs.play_station_generation
      : base.playStationGeneration;

  if (!playStationGeneration) {
    const inferred = inferPlayStationGenerationFromTitle(options?.title);
    if (inferred) playStationGeneration = inferred;
  }

  let playStationEdition: PlayStationEdition | "" = isPlayStationEdition(
    attrs.play_station_edition,
  )
    ? attrs.play_station_edition
    : base.playStationEdition;

  if (!playStationEdition) {
    const inferredEdition = inferPlayStationEditionFromTitle(options?.title);
    if (inferredEdition) playStationEdition = inferredEdition;
  }

  const padRaw = attrs.play_station_pad_count;
  const padCount: number | "" = isPlayStationPadCount(padRaw)
    ? padRaw
    : base.padCount;

  const gamesRaw = attrs.play_station_games_count;
  const gamesCount: number | "" = isPlayStationGamesCount(gamesRaw)
    ? gamesRaw
    : base.gamesCount;

  return { playStationGeneration, playStationEdition, padCount, gamesCount };
}

function pushOptionalCountIssues(
  issues: ListingSpecValidationIssue[],
  key: "play_station_pad_count" | "play_station_games_count",
  value: unknown,
  min: number,
  max: number,
  label: string,
): void {
  if (value === undefined || value === null || value === "") return;
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    !Number.isInteger(value)
  ) {
    issues.push({
      key,
      code: "invalid_type",
      message: `${label} must be a whole number.`,
    });
    return;
  }
  if (value < min || value > max) {
    issues.push({
      key,
      code: "out_of_range",
      message: `${label} must be between ${min} and ${max}.`,
    });
  }
}

export function validateStoredPlayStationAttributes(
  attributes: ListingSpecAttributes | Record<string, unknown>,
): ListingSpecValidationIssue[] {
  const issues: ListingSpecValidationIssue[] = [];
  const attrs = attributes as ListingSpecAttributes;
  const generation = attrs.play_station_generation;

  if (generation === undefined || generation === null || generation === "") {
    issues.push({
      key: "play_station_generation",
      code: "required",
      message: "Choose PS2, PS3, PS4, or PS5.",
    });
  } else if (!isPlayStationGeneration(generation)) {
    issues.push({
      key: "play_station_generation",
      code: "invalid_enum",
      message: "Generation must be PS2, PS3, PS4, or PS5.",
    });
  }

  pushOptionalCountIssues(
    issues,
    "play_station_pad_count",
    attrs.play_station_pad_count,
    PLAY_STATION_PAD_COUNT_MIN,
    PLAY_STATION_PAD_COUNT_MAX,
    "Number of pads",
  );
  pushOptionalCountIssues(
    issues,
    "play_station_games_count",
    attrs.play_station_games_count,
    PLAY_STATION_GAMES_COUNT_MIN,
    PLAY_STATION_GAMES_COUNT_MAX,
    "Number of games",
  );

  const edition = attrs.play_station_edition;
  if (edition !== undefined && edition !== null && edition !== "") {
    if (!isPlayStationEdition(edition)) {
      issues.push({
        key: "play_station_edition",
        code: "invalid_enum",
        message: "Edition must be Slim, Pro, Fat, or Digital.",
      });
    }
  }

  return issues;
}

/** Merge generation into existing attributes for retag/backfill. */
export function mergePlayStationGenerationAttr(
  attributes: unknown,
  generation: PlayStationGeneration,
): Record<string, unknown> {
  const base =
    attributes && typeof attributes === "object" && !Array.isArray(attributes)
      ? { ...(attributes as Record<string, unknown>) }
      : {};
  base.play_station_generation = generation;
  return base;
}
